import { useNavigate, useParams } from "react-router-dom";
import { theme } from "../../../theme";
import Card from "../../atoms/Card";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";
import { AddIcon } from "../../atoms/icons/AddIcon";
import styled from "styled-components";
import { Col, Row } from "react-grid-system";
import { HorizontalSpacer } from "../../atoms/Spacers";
import { useClientTemplates } from "../../../graphql/hooks/documentHandler/useClientTemplates";
import BodySmall from "../../atoms/typography/BodySmall";
import dayjs from "dayjs";
import CircleButton from "../../atoms/buttons/CircleButton";
import { EditIcon } from "../../atoms/icons/EditIcon";
import IconButton from "../../atoms/buttons/IconButton";
import LoadingSpinner from "../../atoms/LoadingSpinner";
import Alert from "../../molecules/Alert";
import NoContentView from "../../molecules/NoContentView";
import { TestIcon } from "../../atoms/icons/TestIcon";
import {
  getTemplateEInvoiceStatusBadge,
  getTemplateLanguageLabelKey,
  getTemplateStatusBadge,
  getTemplateTypeLabelKey,
} from "../../../utils/templateHelpers";
import { useTranslation } from "react-i18next";
import TableHeaderText from "../../atoms/typography/TableHeaderText";
import DeleteModal from "../../molecules/modals/DeleteModal";
import { ClientTemplatesRoute } from "../../../utils/routes";
import { useState } from "react";
import { fileToByteArray } from "../../../utils/file";
import { useCreateTemplateWithFileContent } from "../../../graphql/hooks/invoiceInterpretationTemplates/useCreateTemplateWithFileContent";
import Modal from "../../molecules/Modal";
import TextField from "../../atoms/inputs/TextField";
import RadioButtonBox from "../../atoms/inputs/RadioButtonBox";
import type { InvoiceReaderTemplateType } from "../../../models/ClientInvoiceReaderTemplateModel";
import UploadFile from "../../molecules/UploadFiles";
import DefaultButton from "../../atoms/buttons/DefaultButton";
import ActionButton from "../../atoms/buttons/ActionButton";
import Subtitle from "../../atoms/typography/Subtitle";
import { TrashIcon } from "../../atoms/icons/TrashIcon";
import type { Language } from "../../../models/Language";
import { useDeleteTemplate } from "../../../graphql/hooks/invoiceInterpretationTemplates/useDeleteTemplate";
import TestTemplate from "../../organisms/template/TestTemplate";
import TableHeader from "../../atoms/TableHeader";
import TableRow from "../../atoms/TableRow";
import CardBody from "../../atoms/CardBody";

const FlexedDivHug = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;
`;

const ClientInvoiceTemplates = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { client, error, loading } = useClientTemplates(id || "");

  const [createNewTemplateOpen, setCreateNewTemplateOpen] = useState(false);
  const [file, setFile] = useState<File | undefined>(undefined);
  const [templateType, setTemplateType] = useState<
    InvoiceReaderTemplateType | undefined
  >(undefined);
  const [templateName, setTemplateName] = useState("");
  const [templateLanguage, setTemplateLanguage] = useState<
    Language | undefined
  >(undefined);
  const [errorMessage, setErrorMessage] = useState("");
  const [deleteTemplateErrorMessage, setDeleteTemplateErrorMessage] =
    useState("");
  const [testTemplateModalOpen, setTestTemplateModalOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  );

  const onSuccessCreateTemplate = (result?: any) => {
    setCreateNewTemplateOpen(false);
    setFile(undefined);
    setTemplateName("");
    setTemplateType(undefined);
    setTemplateLanguage(undefined);
    console.log("Template created successfully:", result);
  };
  const onErrorCreateTemplate = (e?: any) => {
    setErrorMessage(
      t("templates.errors.couldNotCreateTemplate" + (e?.message || "")),
    );
  };

  const {
    createTemplateWithFileContent,
    loading: creatingTemplateLoading,
    error: creatingTemplateError,
  } = useCreateTemplateWithFileContent({
    onCompleted: onSuccessCreateTemplate,
    onError: onErrorCreateTemplate,
    clientIdentifier: id || "",
  });

  const createNewTemplate = async () => {
    if (!file || !templateType || !templateLanguage) return;
    setErrorMessage("");
    try {
      const content = await fileToByteArray(file);
      const input = {
        content,
        fileName: file.name,
        templateLanguage,
        templateName,
        templateType,
      };

      await createTemplateWithFileContent({
        variables: { input, clientIdentifier: id || "" },
      });
    } catch (e) {
      setErrorMessage(
        t("templates.errors.couldNotCreateTemplate" + (e?.toString() || "")),
      );
    }
  };

  const onCompletedDeleteTemplate = (result: any) => {
    console.log("Template deleted successfully:", result);
    if (result?.invoiceInterpretation?.deleteTemplate?.success) {
      setErrorMessage("");
    } else {
      setDeleteTemplateErrorMessage(
        t("templates.errors.couldNotDeleteTemplate"),
      );
    }
  };
  const onErrorDeleteTemplate = (error: any) => {
    console.error("Error deleting template:", error);
    setErrorMessage(
      t("templates.errors.couldNotDeleteTemplate" + (error?.toString() || "")),
    );
  };
  const {
    deleteTemplate,
    error: deleteTemplateError,
    loading: deleteTemplateLoading,
  } = useDeleteTemplate({
    onCompleted: onCompletedDeleteTemplate,
    onError: onErrorDeleteTemplate,
  });

  const onClickDeleteTemplate = async (templateId: string) => {
    try {
      await deleteTemplate({
        variables: { templateId, clientIdentifier: id || "" },
      });
    } catch (e) {
      console.error("Error deleting template:", e);
      setErrorMessage(
        t("templates.errors.couldNotDeleteTemplate" + (e?.toString() || "")),
      );
    }
  };

  const onClickGoToEditTemplate = (templateId: string) => {
    navigate(`/client/${id}/invoices/templates/edit/${templateId}`, {
      state: {
        from: {
          routeId: ClientTemplatesRoute.id,
          params: { id },
        },
      },
    });
  };

  const handleUploadFile = (newFile: File) => {
    setFile(newFile);
    if (Array.isArray(newFile)) {
      setFile(newFile[0]);
    } else {
      setFile(newFile);
    }
  };

  return (
    <div>
      {loading && <LoadingSpinner />}
      {(error || errorMessage) && (
        <Alert
          type="error"
          title={t("errors.somethingWentWrong")}
          message={errorMessage || error?.toString()}
        />
      )}
      <Row nogutter justify="end" align="center">
        <FlexedDivHug>
          <OutlinedButton
            color={theme.colors.primary}
            title={t("templates.newTemplate")}
            medium
            icon={<AddIcon color={theme.colors.primary} />}
            onClick={() => setCreateNewTemplateOpen(true)}
          />
          {/*<OutlinedButton
            color={theme.colors.primary}
            title={t("templates.newInvoice")}
            medium
            icon={<AddIcon color={theme.colors.primary} />}
          />
          */}
        </FlexedDivHug>
      </Row>
      <HorizontalSpacer spacing={1.5} />
      <Card spacing={0} border>
        {!loading && client?.invoiceReaderTemplates?.length === 0 && (
          <CardBody>
            <NoContentView />
          </CardBody>
        )}
        <TableHeader>
          <Col lg={2}>
            <TableHeaderText>{t("random.name")}</TableHeaderText>
          </Col>
          <Col lg={1}>
            <TableHeaderText>{t("random.type")}</TableHeaderText>
          </Col>
          <Col lg={1.5}>
            <TableHeaderText>{t("common.status")}</TableHeaderText>
          </Col>
          <Col lg={1.5}>
            <TableHeaderText>
              {t("common.latestEdited")}
            </TableHeaderText>
          </Col>
          <Col lg={1}>
            <TableHeaderText>{t("templates.rowsInterpreted")}</TableHeaderText>
          </Col>
          <Col lg={1.5}>
            <TableHeaderText>{t("random.eInvoice")}</TableHeaderText>
          </Col>
          <Col lg={1.5}>
            <TableHeaderText>{t("aml.comment")}</TableHeaderText>
          </Col>
          <Col lg={2} />
        </TableHeader>

        {client?.invoiceReaderTemplates?.map((template) => (
          <TableRow key={template.id} align="center">
            <Col lg={2}>
              <BodySmall>{template?.name}</BodySmall>
            </Col>
            <Col lg={1}>
              <BodySmall>
                {t(getTemplateTypeLabelKey(template?.templateType))}
              </BodySmall>
              <BodySmall>
                {t(getTemplateLanguageLabelKey(template?.templateLanguage))}
              </BodySmall>
            </Col>
            <Col lg={1.5}>{getTemplateStatusBadge(template.state)}</Col>
            <Col lg={1.5}>
              <BodySmall>
                {dayjs(template?.modifiedAt).format("YYYY-MM-DD")}
              </BodySmall>
              <BodySmall>
                {dayjs(template?.modifiedAt).format("HH:mm")}
              </BodySmall>
            </Col>
            <Col lg={1}>
              <BodySmall>{template?.mappedRowFieldCount}</BodySmall>
            </Col>
            <Col lg={1.5}>
              {getTemplateEInvoiceStatusBadge(template.eInvoiceTested)}
            </Col>
            <Col lg={1.5}>
              <BodySmall>{template?.notes}</BodySmall>
            </Col>
            <Col lg={2}>
              <Row justify="end" nogutter>
                <FlexedDivHug>
                  <CircleButton
                    icon={<EditIcon />}
                    onClick={() => onClickGoToEditTemplate(template.id)}
                  />
                  <IconButton
                    icon={<TestIcon color={theme.colors.info} />}
                    onClick={() => {
                      setSelectedTemplateId(template.id);
                      setTestTemplateModalOpen(true);
                    }}
                    color={theme.colors.info20}
                  />
                  <DeleteModal
                    deleteButtonType="iconButton"
                    onClickDelete={() => onClickDeleteTemplate(template.id)}
                    loading={deleteTemplateLoading}
                    errorMesssage={
                      deleteTemplateErrorMessage ?? deleteTemplateError?.message
                    }
                  />
                </FlexedDivHug>
              </Row>
            </Col>
          </TableRow>
        ))}
      </Card>

      {createNewTemplateOpen && (
        <Modal onClose={() => setCreateNewTemplateOpen(false)} closeIcon>
          <Subtitle color={theme.colors.primary} textalign="center">
            {t("documentHandler.createNewTemplate")}
          </Subtitle>
          <HorizontalSpacer spacing={2} />
          <UploadFile
            text={t("documentHandler.dragAndDropOrClickToUploadDocument")}
            onDrop={handleUploadFile}
            multiple={false}
          />
          <HorizontalSpacer spacing={1} />
          {file && (
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <BodySmall fontweight={700}>{file?.name}</BodySmall>
              <IconButton
                icon={<TrashIcon color={theme.colors.error} />}
                color={theme.colors.error20}
                onClick={() => setFile(undefined)}
                size="medium"
              />
            </div>
          )}
          <HorizontalSpacer spacing={2} />
          <TextField
            fullWidth
            label={t("documentHandler.templateName")}
            placeholder={t("documentHandler.inputTemplateName")}
            value={templateName}
            onChange={(value) => setTemplateName(value)}
            disabled={creatingTemplateLoading}
          />
          <HorizontalSpacer spacing={2} />
          <BodySmall>{t("documentHandler.selectTemplateType")}</BodySmall>
          <HorizontalSpacer spacing={0.5} />
          <div>
            <div
              style={{
                display: "flex",
                gap: 24,
                justifyContent: "space-around",
              }}
            >
              <RadioButtonBox
                title={t("documentHandler.credit")}
                checked={templateType === "CREDIT"}
                onChange={() => setTemplateType("CREDIT")}
                disabled={creatingTemplateLoading}
              />
              <RadioButtonBox
                title={t("documentHandler.debit")}
                checked={templateType === "DEBIT"}
                onChange={() => setTemplateType("DEBIT")}
                disabled={creatingTemplateLoading}
              />
              <RadioButtonBox
                title={t("documentHandler.debitMaskAndStampOnly")}
                checked={templateType === "DEBIT_MASK_AND_STAMP_ONLY"}
                onChange={() => setTemplateType("DEBIT_MASK_AND_STAMP_ONLY")}
                disabled={creatingTemplateLoading}
              />
            </div>
          </div>

          <HorizontalSpacer spacing={2} />
          <BodySmall>{t("random.selectLanguage")}</BodySmall>
          <HorizontalSpacer spacing={0.5} />
          <div style={{ display: "flex", gap: 24 }}>
            <RadioButtonBox
              title="Svenska"
              checked={templateLanguage === "SWEDISH"}
              onChange={() => setTemplateLanguage("SWEDISH")}
              disabled={creatingTemplateLoading}
            />
            <RadioButtonBox
              title="English"
              checked={templateLanguage === "ENGLISH"}
              onChange={() => setTemplateLanguage("ENGLISH")}
              disabled={creatingTemplateLoading}
            />
          </div>

          <HorizontalSpacer spacing={1} />
          {creatingTemplateError && (
            <Alert
              type="error"
              message={creatingTemplateError.message}
              title={t("errors.somethingWentWrong")}
            />
          )}
          <HorizontalSpacer spacing={1} />
          <div style={{ display: "flex", justifyContent: "right", gap: 16 }}>
            <DefaultButton
              title={t("common.cancel")}
              disabled={creatingTemplateLoading}
            />
            <ActionButton
              title={t("documentHandler.createNewTemplate")}
              color={theme.colors.primary}
              loading={creatingTemplateLoading}
              onClick={createNewTemplate}
              disabled={
                !templateName ||
                !templateType ||
                !templateLanguage ||
                !file ||
                creatingTemplateLoading
              }
            />
          </div>
        </Modal>
      )}

      {testTemplateModalOpen && selectedTemplateId && (
        <Modal
          onClose={() => setTestTemplateModalOpen(false)}
          closeIcon
          width={1200}
        >
          <TestTemplate
            templateId={selectedTemplateId}
            clientIdentifier={id ?? "noClient"}
            onClose={() => setTestTemplateModalOpen(false)}
          />
        </Modal>
      )}
    </div>
  );
};

export default ClientInvoiceTemplates;
